import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { getThemeColors, THEME_OPTIONS } from '../theme'
import { getKeywordLimit, getUserFeatures } from '../lib/userFeatures'

const TOTAL_STEPS = 4
const MAX_KEYWORDS = 5

const PAISES = ['México', 'Colombia', 'Argentina', 'Chile', 'Perú', 'Brasil', 'España', 'Remoto LATAM']

const CIUDADES = {
  'México': [
    'Ciudad de México', 'Aguascalientes', 'Baja California', 'Baja California Sur',
    'Campeche', 'Chiapas', 'Chihuahua', 'Coahuila', 'Colima', 'Durango',
    'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Estado de México',
    'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
    'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
    'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz',
    'Yucatán', 'Zacatecas', 'Remoto',
  ],
  'Colombia':     ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Remoto'],
  'Argentina':    ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Tucumán', 'Remoto'],
  'Chile':        ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Remoto'],
  'Perú':         ['Lima', 'Arequipa', 'Trujillo', 'Cusco', 'Remoto'],
  'Brasil':       ['São Paulo', 'Rio de Janeiro', 'Belo Horizonte', 'Curitiba', 'Porto Alegre', 'Remoto'],
  'España':       ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Bilbao', 'Remoto'],
  'Remoto LATAM': ['Remoto LATAM'],
}

async function extractTextFromFile(file) {
  const ext = file.name.split('.').pop().toLowerCase()
  if (ext === 'txt') {
    return new Promise((res, rej) => {
      const r = new FileReader()
      r.onload = e => res(e.target.result)
      r.onerror = rej
      r.readAsText(file)
    })
  }
  if (ext === 'pdf') {
    try {
      const pdfjsLib = await import('pdfjs-dist')
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString()
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      let text = ''
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const content = await page.getTextContent()
        text += content.items.map(item => item.str).join(' ') + '\n'
      }
      return text
    } catch (e) {
      console.error('PDF error:', e)
      return ''
    }
  }
  if (ext === 'doc' || ext === 'docx') {
    try {
      const mammoth = await import('mammoth')
      const arrayBuffer = await file.arrayBuffer()
      const result = await mammoth.extractRawText({ arrayBuffer })
      return result.value
    } catch (e) {
      console.error('Word error:', e)
      return ''
    }
  }
  return ''
}

/* eslint-disable no-control-regex */
function limpiarTexto(texto) {
  if (!texto) return ''
  const sanitized = Array.from(texto).filter((char) => {
    const code = char.codePointAt(0)
    if (code === 9 || code === 10 || code === 13) return true
    if (code < 32) return false
    if (code >= 55296 && code <= 57343) return false
    if (code === 65534 || code === 65535) return false
    return true
  }).join('')

  return sanitized.trim()
}
/* eslint-enable no-control-regex */

export default function Onboarding({ theme = THEME_OPTIONS.LIGHT }) {
  const C = getThemeColors(theme)
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [cvFile, setCvFile] = useState(null)
  const [cvText, setCvText] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingKeywords, setLoadingKeywords] = useState(false)
  const [error, setError] = useState('')
  const [keywordInput, setKeywordInput] = useState('')
  const [keywords, setKeywords] = useState([])
  const [triggerStatus, setTriggerStatus] = useState(null) // null | 'running' | 'done' | 'error'
  const [keywordLimit, setKeywordLimit] = useState(3)
  const [form, setForm] = useState({
    nombre: '', sector: 'Banca/Fintech',
    pais: 'México', ciudad: 'Ciudad de México',
    modalidad: 'Híbrido', seniority: 'Senior',
  })

  useEffect(() => {
    const loadPerfil = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      const { data } = await supabase.from('profiles').select('nombre').eq('id', user.id).single()
      if (data?.nombre) setForm(f => ({ ...f, nombre: data.nombre }))
      try {
        const features = await getUserFeatures(user.id)
        const limit = getKeywordLimit(features)
        setKeywordLimit(limit === null ? MAX_KEYWORDS : Math.min(limit, MAX_KEYWORDS))
      } catch (featureError) {
        console.warn('No se pudo cargar feature flags para keyword limit, usando default:', featureError)
        setKeywordLimit(3)
      }
    }
    loadPerfil()
  }, [])

  const handlePaisChange = (pais) => {
    setForm(f => ({ ...f, pais, ciudad: (CIUDADES[pais] || [])[0] || '' }))
  }

  const handleFile = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setCvFile(file)
    setExtracting(true)
    const text = await extractTextFromFile(file)
    setCvText(limpiarTexto(text))
    setExtracting(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const files = e.dataTransfer.files
    if (files && files[0]) {
      const file = files[0]
      const validTypes = ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      const validExts = ['.pdf', '.txt', '.doc', '.docx']
      const ext = '.' + file.name.split('.').pop().toLowerCase()
      
      if (validTypes.includes(file.type) || validExts.includes(ext)) {
        setCvFile(file)
        setExtracting(true)
        extractTextFromFile(file).then(text => {
          setCvText(limpiarTexto(text))
          setExtracting(false)
        })
      } else {
        setError('Por favor carga un archivo PDF, Word o TXT.')
      }
    }
  }

  const handleSuggestKeywords = async () => {
    setLoadingKeywords(true)
    setError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const { data, error: fnError } = await supabase.functions.invoke('extract-keywords', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { cvText: cvText.slice(0, 4000), nombre: form.nombre, sector: form.sector, plan: 'free' },
      })
      if (fnError) throw fnError
      if (data?.keywords) setKeywords(data.keywords.slice(0, MAX_KEYWORDS))
    } catch (err) {
      console.error('ERROR keywords:', err)
      setError('No se pudieron sugerir keywords. Puedes agregarlas manualmente.')
    }
    setLoadingKeywords(false)
  }

  const addKeyword = () => {
    const kw = keywordInput.trim()
    if (!kw || keywords.length >= keywordLimit || keywords.includes(kw)) return
    setKeywords([...keywords, kw])
    setKeywordInput('')
  }

  const removeKeyword = (kw) => setKeywords(keywords.filter(k => k !== kw))

  // ── Trigger scraper via Edge Function ────────────────────────────────────────
  const triggerScraper = async (userId) => {
    setTriggerStatus('running')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('No se encontro una sesion valida para disparar la busqueda.')

      const { error: fnError } = await supabase.functions.invoke('run-scraper', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: { user_id: userId, source: 'auto' },
      })
      if (fnError) throw fnError
      setTriggerStatus('done')
      return { ok: true }
    } catch (err) {
      console.warn('Scraper trigger failed (non-critical):', err)
      setTriggerStatus('error')
      return { ok: false, error: err }
    }
  }

  const handleGenerate = async () => {
    if (!form.nombre) { setError('Agrega tu nombre para continuar.'); return }
    if (keywords.length === 0) { setError('Agrega al menos una keyword de búsqueda.'); return }
    setLoading(true)
    setError('')
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setError('Sesión expirada. Vuelve a iniciar sesión.'); setLoading(false); return }

      const perfilGenerado = {
        nombre: form.nombre, seniority: form.seniority,
        sectores: [form.sector], keywords_busqueda: keywords.slice(0, keywordLimit),
        resumen_perfil: `Profesional en ${form.sector} buscando oportunidades en ${form.ciudad}`,
      }

      await supabase.from('profiles').upsert({ id: user.id, nombre: form.nombre }, { onConflict: 'id' })

      const { error: dbError } = await supabase
        .from('search_profiles')
        .upsert({
          user_id: user.id, cv_texto: cvText, perfil_generado: perfilGenerado,
          keywords: keywords.slice(0, keywordLimit), sectores: [form.sector],
          pais: form.pais, ubicacion: form.ciudad,
          modalidad: form.modalidad, seniority: form.seniority,
          herramientas: [], updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' })

      if (dbError) throw dbError

      // Disparar scraper inmediatamente para este usuario y dar feedback visible
      const triggerResult = await triggerScraper(user.id)
      setLoading(false)
      if (!triggerResult.ok) {
        setError('Tu perfil se guardo bien, pero no pudimos iniciar la busqueda automatica todavia. Puedes entrar al dashboard y reintentar mas tarde.')
      }
      setTimeout(() => navigate('/dashboard'), 1200)
      return
    } catch (err) {
      console.error('ERROR guardar perfil:', err)
      if (String(err?.message || '').toLowerCase().includes('keyword limit exceeded')) {
        setError(`Tu plan actual permite hasta ${keywordLimit} keywords. Ajusta tu lista e intenta de nuevo.`)
      } else {
        setError('Error al guardar tu perfil. Intenta de nuevo.')
      }
    }
    setLoading(false)
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: theme === THEME_OPTIONS.DARK ? '#0f172a' : C.bg,
    border: `0.5px solid ${C.border}`, borderRadius: '8px',
    color: C.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  }
  const selectStyle = { ...inputStyle, background: C.white }
  const labelStyle = { display: 'block', fontSize: '12px', color: C.muted, marginBottom: '6px', marginTop: '14px' }
  const btnPrimary = {
    width: '100%', padding: '12px', background: C.blue, color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '500', marginTop: '20px',
  }
  const btnSecondary = {
    flex: 1, padding: '12px', background: C.white, color: C.muted,
    border: `0.5px solid ${C.border}`, borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px',
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '16px', padding: '2.5rem', width: '100%', maxWidth: '560px' }}>

        <div style={{ fontSize: '14px', fontWeight: '500', color: C.text, marginBottom: '1.5rem' }}>
          Job<span style={{ color: C.blue }}>Pipeline</span>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2rem' }}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <React.Fragment key={i}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: step >= i + 1 ? C.blue : C.bg,
                border: `0.5px solid ${step >= i + 1 ? C.blue : C.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: '500',
                color: step >= i + 1 ? '#fff' : C.subtle, flexShrink: 0,
              }}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              {i < TOTAL_STEPS - 1 && (
                <div style={{ flex: 1, height: '1px', background: step > i + 1 ? C.blue : C.border }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* PASO 1: CV */}
        {step === 1 && (
          <>
            <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, letterSpacing: '-0.5px', marginBottom: '6px' }}>Sube tu CV</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '24px', lineHeight: '1.6' }}>
              Nyx.ia analiza tu CV para sugerirte las mejores keywords de búsqueda.
            </div>
            <label htmlFor="cv-upload">
              <div 
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                style={{ border: `1.5px dashed ${cvFile ? C.blue : C.border}`, borderRadius: '10px', padding: '32px', textAlign: 'center', cursor: 'pointer', background: cvFile ? C.blueLight : C.bg, transition: 'all 0.2s' }}>
                {extracting ? (
                  <div style={{ color: C.blue, fontSize: '13px' }}>📄 Leyendo CV...</div>
                ) : cvFile ? (
                  <>
                    <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>✅</div>
                    <div style={{ color: C.blue, fontWeight: '500', fontSize: '13px' }}>{cvFile.name}</div>
                    <div style={{ fontSize: '11px', color: C.subtle, marginTop: '4px' }}>Haz clic para cambiar</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📄</div>
                    <div style={{ color: C.blue, fontWeight: '500', fontSize: '13px', marginBottom: '4px' }}>Arrastra tu CV aquí</div>
                    <div style={{ fontSize: '12px', color: C.subtle }}>PDF, Word o TXT</div>
                  </>
                )}
              </div>
            </label>
            <input id="cv-upload" type="file" accept=".pdf,.txt,.doc,.docx" style={{ display: 'none' }} onChange={handleFile} />
            <div style={{ textAlign: 'center', color: C.subtle, margin: '14px 0', fontSize: '12px' }}>— o continúa sin CV —</div>
            <button style={btnPrimary} onClick={() => setStep(2)}>
              {cvFile ? 'Continuar con este CV' : 'Completar manualmente'}
            </button>
          </>
        )}

        {/* PASO 2: Perfil */}
        {step === 2 && (
          <>
            <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, letterSpacing: '-0.5px', marginBottom: '6px' }}>Tu perfil de búsqueda</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '24px' }}>
              {cvFile ? 'Confirma o ajusta tus datos.' : 'Cuéntanos sobre ti.'}
            </div>
            <label style={labelStyle}>Tu nombre</label>
            <input style={inputStyle} placeholder="Tu nombre completo" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={labelStyle}>Sector</label>
                <select style={selectStyle} value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })}>
                  {['Banca/Fintech', 'Seguros', 'Consultoría', 'Tecnología', 'Retail', 'Manufactura', 'Salud', 'Educación'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Seniority</label>
                <select style={selectStyle} value={form.seniority} onChange={e => setForm({ ...form, seniority: e.target.value })}>
                  {['Junior', 'Semi-senior', 'Senior', 'Manager', 'Director', 'VP', 'C-Level'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={labelStyle}>País</label>
                <select style={selectStyle} value={form.pais} onChange={e => handlePaisChange(e.target.value)}>
                  {PAISES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Ciudad / Estado</label>
                <select style={selectStyle} value={form.ciudad} onChange={e => setForm({ ...form, ciudad: e.target.value })}>
                  {(CIUDADES[form.pais] || []).map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <label style={labelStyle}>Modalidad</label>
            <select style={selectStyle} value={form.modalidad} onChange={e => setForm({ ...form, modalidad: e.target.value })}>
              {['Híbrido', 'Remoto', 'Presencial'].map(o => <option key={o}>{o}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button style={btnSecondary} onClick={() => setStep(1)}>← Atrás</button>
              <button style={{ ...btnPrimary, flex: 2, marginTop: 0 }} onClick={() => { setError(''); setStep(3) }}>Continuar</button>
            </div>
            {!loading && (triggerStatus === 'done' || triggerStatus === 'error') && (
              <button style={{ ...btnSecondary, width: '100%', marginTop: '12px' }} onClick={() => navigate('/dashboard')}>
                Ir al panel
              </button>
            )}
          </>
        )}

        {/* PASO 3: Keywords */}
        {step === 3 && (
          <>
            <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, letterSpacing: '-0.5px', marginBottom: '6px' }}>Tus keywords de búsqueda</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '20px', lineHeight: '1.6' }}>
              Definimos qué roles buscar. Plan gratuito: hasta {MAX_KEYWORDS} keywords.
            </div>
            {(cvText || form.nombre) && (
              <button onClick={handleSuggestKeywords} disabled={loadingKeywords} style={{ width: '100%', padding: '10px', background: loadingKeywords ? C.bg : C.blueLight, color: C.blue, border: `0.5px solid #BFDBFE`, borderRadius: '8px', cursor: loadingKeywords ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500', marginBottom: '16px' }}>
                {loadingKeywords ? '🤖 Nyx.ia está analizando tu CV...' : '✨ Sugerir keywords con Nyx.ia'}
              </button>
            )}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                placeholder="ej: Project Manager Senior"
                value={keywordInput}
                onChange={e => setKeywordInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addKeyword()}
                disabled={keywords.length >= keywordLimit}
              />
              <button onClick={addKeyword} disabled={keywords.length >= keywordLimit || !keywordInput.trim()} style={{ padding: '10px 16px', background: keywords.length >= keywordLimit ? C.bg : C.blue, color: keywords.length >= keywordLimit ? C.subtle : '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap' }}>
                + Agregar
              </button>
            </div>
            <div style={{ minHeight: '80px', background: C.bg, borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
              {keywords.length === 0 ? (
                <div style={{ fontSize: '12px', color: C.subtle, textAlign: 'center', paddingTop: '16px' }}>
                  Agrega keywords o usa el botón de Nyx.ia ↑
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {keywords.map(kw => (
                    <div key={kw} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: C.blueLight, color: C.blue, border: `0.5px solid #BFDBFE`, borderRadius: '20px', padding: '4px 12px', fontSize: '13px' }}>
                      {kw}
                      <span onClick={() => removeKeyword(kw)} style={{ cursor: 'pointer', color: C.subtle, fontWeight: '500', fontSize: '14px', lineHeight: 1 }}>×</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: keywords.length >= keywordLimit ? C.amber : C.subtle, marginBottom: '4px' }}>
              {keywords.length}/{keywordLimit} keywords · {keywords.length >= keywordLimit ? 'Límite alcanzado — actualiza a Premium para más' : `Puedes agregar ${keywordLimit - keywords.length} más`}
            </div>
            {error && <div style={{ background: C.redBg, border: `0.5px solid #FECACA`, color: C.red, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginTop: '8px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button style={btnSecondary} onClick={() => setStep(2)}>← Atrás</button>
              <button style={{ ...btnPrimary, flex: 2, marginTop: 0, opacity: keywords.length === 0 ? 0.5 : 1 }} onClick={() => { if (keywords.length > 0) { setError(''); setStep(4) } else setError('Agrega al menos una keyword.') }}>
                Continuar
              </button>
            </div>
          </>
        )}

        {/* PASO 4: Resumen */}
        {step === 4 && (
          <>
            <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, letterSpacing: '-0.5px', marginBottom: '6px' }}>¡Todo listo!</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '20px', lineHeight: '1.6' }}>
              Confirma tu configuración y empezamos a buscar vacantes.
            </div>
            <div style={{ background: C.bg, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
              {[
                ['📄', 'CV',        cvFile ? cvFile.name : 'Sin CV'],
                ['👤', 'Nombre',    form.nombre || '—'],
                ['🏢', 'Sector',    form.sector],
                ['🌎', 'País',      form.pais],
                ['📍', 'Ciudad',    form.ciudad],
                ['💼', 'Modalidad', form.modalidad],
              ].map(([icon, label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `0.5px solid ${C.border}`, fontSize: '13px' }}>
                  <span style={{ color: C.subtle }}>{icon} {label}</span>
                  <span style={{ color: C.text, fontWeight: '500' }}>{value}</span>
                </div>
              ))}
              <div style={{ padding: '10px 0 4px' }}>
                <div style={{ fontSize: '12px', color: C.subtle, marginBottom: '8px' }}>🔍 Keywords de búsqueda</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {keywords.map(kw => (
                    <span key={kw} style={{ background: C.blueLight, color: C.blue, borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: '500' }}>{kw}</span>
                  ))}
                </div>
              </div>
            </div>

            {!form.nombre && (
              <div style={{ background: '#fffbeb', border: '0.5px solid #fde68a', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: C.amber, marginBottom: '12px' }}>
                ⚠ No agregaste tu nombre.{' '}
                <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setStep(2)}>Regresar y agregarlo</span>
              </div>
            )}

            {error && <div style={{ background: C.redBg, border: `0.5px solid #FECACA`, color: C.red, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

            {/* Estado del trigger del scraper */}
            {triggerStatus === 'running' && (
              <div style={{ background: C.blueLight, border: `0.5px solid #BFDBFE`, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: C.blue, marginBottom: '12px', textAlign: 'center' }}>
                🔍 Buscando tus primeras vacantes...
              </div>
            )}
            {triggerStatus === 'done' && (
              <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: C.green, marginBottom: '12px', textAlign: 'center' }}>
                ✅ ¡Búsqueda iniciada! Las vacantes llegarán en unos minutos. La primera corrida tarda de 1 a 5 minutos. Si aún no ves resultados en el dashboard, actualiza la página.
              </div>
            )}
            {triggerStatus === 'error' && (
              <div style={{ background: C.redBg, border: '0.5px solid #FECACA', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: C.red, marginBottom: '12px', textAlign: 'center' }}>
                No pudimos iniciar la busqueda automatica en este momento.
              </div>
            )}
            {loading && triggerStatus === null && (
              <div style={{ background: C.blueLight, border: `0.5px solid #BFDBFE`, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: C.blue, marginBottom: '12px', textAlign: 'center' }}>
                💾 Guardando tu perfil...
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={btnSecondary} onClick={() => setStep(3)} disabled={loading}>← Atrás</button>
              <button
                style={{ ...btnPrimary, flex: 2, marginTop: 0, opacity: (loading || !form.nombre) ? 0.6 : 1, cursor: (loading || !form.nombre) ? 'not-allowed' : 'pointer' }}
                onClick={handleGenerate}
                disabled={loading || !form.nombre}
              >
                {loading ? 'Iniciando búsqueda...' : '🚀 Comenzar búsqueda'}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  )
}

